import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { User, Shield, Notebook, History, Calculator, FileText } from "lucide-react";
import { InsuraBotIcon } from "@/app/_module/components/icons/InsuraBotIcon";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import api from "@/app/_module/services/api";
import { endpoint } from "@/app/_module/services/endpoints";
import { getApiUrl } from "@/app/_module/services/config";
import { InsuraBot } from "@/app/_module/components/InsuraBot";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";

import type { Policy, LeadWithPolicies } from "../types/lead-with-policies-type";

import { LeadNotes } from "./LeadNotes";
import { LeadPolicyDetails } from "./LeadPolicyDetails";
import { MiscellaneousSection } from "./MiscellaneousSection";
import { LeadHistory } from "./LeadHistory";
import { LeadDocuments } from "./LeadDocuments";
import { LeadActivityTimeline } from "./LeadActivityTimeline";
import { LeadInstantQuotes } from "./LeadInstantQuotes";
import { BeneficiarySection } from "./BeneficiarySection";
import { ErsSection } from "./ErsSection";
import LeadHouseholdSection from "./LeadHouseholdSection";
import { UnifiedGeneralCard } from "./UnifiedGeneralCard";
import { useLeadCall } from "@/app/(authenticated)/leads/_modules/hooks/useLeadCall";
import { LeadAppointmentsSummary } from "./LeadAppointmentsSummary";
import { useEmails, useSyncLeadEmails } from "@/app/(authenticated)/communication/_modules/hooks/use-hook";
import type { EmailHistory } from "@/app/(authenticated)/communication/_modules/types/email-history.types";
import { EmailDetailModal } from "./EmailDetailModal";

interface LeadDetailsLayoutProps {
  isEditing: boolean;
  leadId?: string;
  hasPolicies: boolean;
  policies: Policy[];
  policyCount: number;
  initialNotes?: any[];
  leadData?: LeadWithPolicies;
  onStatusChange?: (leadId: string, newStatus: string) => Promise<void>; // Handler to sync status changes
  defaultTab?: string; // Optional default tab override (useful for embedded contexts)
  embedded?: boolean; // When true, skip URL sync (for embedded contexts like table rows)
  onCallBack?: (phoneNumber: string) => void; // Handler to initiate callback for inbound calls
  onOpenSmsWidget?: () => void; // Handler to open SMS chat widget
  openInsuraBot?: boolean; // When true, auto-open InsuraBot on mount (for PolicyGuard re-quote)
}

export const LeadDetailsLayout = function ({ isEditing, leadId, hasPolicies, policies, policyCount, initialNotes, leadData, onStatusChange, defaultTab = 'details', embedded = false, onCallBack, onOpenSmsWidget, openInsuraBot }: LeadDetailsLayoutProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isWidgetOpen, isMinimized } = useUnifiedDialer();
  // Dial an alternate number (Phone 2/3, Mobile, Fax) off a custom field —
  // same path as the main Call button, still attributed to this lead.
  const { callLead } = useLeadCall(leadData);

  // Detect laptop-sized screens where dialer covers the right column
  const [isLaptopScreen, setIsLaptopScreen] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1799px)");
    const onChange = () => setIsLaptopScreen(mql.matches);
    mql.addEventListener("change", onChange);
    setIsLaptopScreen(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Only move right column content to left when dialer is open AND screen is small enough
  const showInLeftColumn = isWidgetOpen && !isMinimized && isLaptopScreen;

  // Determine if this is a primary lead or household member
  // Use isHouseholdMember field from backend (more reliable than householdId)
  const isPrimaryLead = !leadData?.isHouseholdMember;

  // Get tab from URL or default to provided defaultTab (or 'details')
  // In embedded mode, always use defaultTab to avoid URL interference
  const [activeTab, setActiveTab] = useState(() => {
    if (embedded) return defaultTab;
    try {
      return searchParams?.get('tab') || defaultTab;
    } catch {
      return defaultTab;
    }
  });

  // Sync URL when tab changes (skip in embedded mode)
  useEffect(() => {
    if (embedded) return; // Don't sync URL when embedded in a table row
    try {
      const currentTab = searchParams?.get('tab') || defaultTab;
      if (currentTab !== activeTab && router) {
        const params = new URLSearchParams(searchParams?.toString() || '');
        params.set('tab', activeTab);
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    } catch {
      // Silently ignore routing errors in embedded mode
    }
  }, [activeTab, searchParams, router, embedded, defaultTab]);

  // Pagination state for call history
  const [callPage, setCallPage] = useState(1);
  const [callPageSize, setCallPageSize] = useState(25);

  // Pagination state for email history
  const [emailPage, setEmailPage] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(25);

  // Pagination state for SMS history
  const [smsPage, setSmsPage] = useState(1);
  const [smsPageSize, setSmsPageSize] = useState(25);

  // InsuraBot state for quotes
  const [isInsuraBotOpen, setIsInsuraBotOpen] = useState(false);
  // Quote & Enroll agencies get the enrollable Techficient quote-form card
  // inside InsuraBot; everyone else gets the Compulife quote flow.
  const { enabled: quoteEnrollEnabled } = useFeatureFlag("quoteAndEnroll");

  // Auto-open InsuraBot when triggered from PolicyGuard re-quote
  useEffect(() => {
    if (openInsuraBot && leadData) {
      setIsInsuraBotOpen(true);
    }
  }, [openInsuraBot, leadData]);

  // Email detail modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailHistory | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);

  // Email sync state - tracks if we've already synced for this lead
  const [hasTriggeredEmailSync, setHasTriggeredEmailSync] = useState(false);
  const syncLeadEmailsMutation = useSyncLeadEmails();

  // Build initial quote message with lead info - includes all Compulife required fields
  const buildQuoteMessage = () => {
    if (!leadData) return "I need help getting life insurance quotes.";

    const name = leadData.name || `${leadData.first_name || ''} ${leadData.last_name || ''}`.trim();
    const parts: string[] = [];

    parts.push(`I need life insurance quotes for my client.`);
    parts.push(`\nClient Information:`);
    if (name) parts.push(`- Name: ${name}`);
    if (leadData.dob) parts.push(`- Date of Birth: ${leadData.dob}`);
    if (leadData.gender) parts.push(`- Gender: ${leadData.gender}`);
    if (leadData.state) parts.push(`- State: ${leadData.state}`);
    if (leadData.zipCode) parts.push(`- ZIP Code: ${leadData.zipCode}`);
    if (leadData.height) parts.push(`- Height: ${leadData.height}`);
    if (leadData.weight) parts.push(`- Weight: ${leadData.weight}`);

    // Note what's missing
    const missing: string[] = [];
    if (!leadData.state) missing.push('State');
    if (!leadData.zipCode) missing.push('ZIP Code');
    if (!leadData.gender) missing.push('Gender');
    if (!leadData.dob) missing.push('Date of Birth');

    if (missing.length > 0) {
      parts.push(`\nMissing Information: ${missing.join(', ')}`);
    }

    // Quote parameters to confirm. Product type is left OPEN so InsuraBot
    // presents the full Compulife catalog (Term 10–40yr, Whole Life + pay
    // variants, Universal Life, GUL, Final Expense, Mortgage Protection,
    // Return of Premium) instead of defaulting to just FE/WL.
    parts.push(`\nQuote Parameters (please confirm before quoting):`);
    parts.push(`- Coverage Amount: Need to confirm with client`);
    parts.push(`- Product Type: Need to confirm with client (ask me which — options include Term Life 10/15/20/25/30-year, Whole Life, Universal Life, Final Expense, Mortgage Protection, Return of Premium)`);
    parts.push(`- Payment Frequency: Monthly (confirm if different)`);
    parts.push(`- Smoker Status: Need to confirm with client`);
    parts.push(`- Health Class: Standard (confirm if different)`);

    parts.push(`\nPlease ask me to confirm the coverage amount, product type, and smoker status before generating quotes. Also ask for any missing information listed above.`);

    return parts.join('\n');
  };

  // Get InsuraBot context from lead data.
  //
  // Quote & Enroll agencies get the exact same flow as the Quote & Enroll
  // page's "Quote with InsuraBot": enrollment mode + an auto-sent "show me the
  // quote form" prompt so the bot renders the pre-filled Techficient quote-form
  // CARD inside the chat (not a chatty back-and-forth). Everyone else falls
  // back to the Compulife quote flow with the detailed confirmation prompt.
  const getLeadContextForInsuraBot = () => {
    if (!leadData) return {};
    const leadName =
      leadData.name ||
      `${leadData.first_name || ''} ${leadData.last_name || ''}`.trim();
    const base = {
      leadId,
      leadName,
      dob: leadData.dob,
      gender: leadData.gender,
      state: leadData.state,
      zipCode: leadData.zipCode || '',
      height: leadData.height,
      weight: leadData.weight,
      phone: leadData.phone,
      email: leadData.email,
      quoteMode: true,
      startQuoting: true,
    };
    if (quoteEnrollEnabled) {
      return {
        ...base,
        enrollmentMode: true,
        autoSendInitial: true,
        initialMessage: `Show me the quote form for ${
          leadName || 'this lead'
        }, pre-filled with what we have on file.`,
      };
    }
    // Compulife path — same in-chat form experience via show_compulife_quote_form.
    // `autoSendInitial` + the "show me the quote form" prompt makes the bot
    // render the Compulife form card immediately (no chatty back-and-forth).
    return {
      ...base,
      autoSendInitial: true,
      initialMessage: `Show me the quote form for ${
        leadName || 'this lead'
      }, pre-filled with what we have on file.`,
    };
  };

  // Clicking Quotes opens InsuraBot with the quote-form card inside (pre-filled
  // from this lead) — the same experience as "Quote with InsuraBot" on the
  // Quote & Enroll page, rather than switching to a separate tab.
  const handleTabChange = (newTab: string) => {
    if (newTab === 'quotes') {
      setIsInsuraBotOpen(true);
      return; // stay on the current tab; the bot opens over it
    }
    setActiveTab(newTab);
  };

  // Fetch call history data with pagination and real-time refresh
  const { data: callHistoryData, isLoading: callHistoryLoading, error: callHistoryError, refetch: refetchCallHistory } = useQuery({
    queryKey: ['lead-call-history', leadId, callPage, callPageSize],
    queryFn: async () => {
      if (!leadId) return { items: [], meta: null };
      const response = await api.get({
        url: getApiUrl(endpoint.communication.getCalls) + `?leadId=${leadId}&page=${callPage}&limit=${callPageSize}`,
        auth: true
      });
      return {
        items: (response as any)?.data?.data?.items || (response as any)?.data?.items || [],
        meta: (response as any)?.data?.data?.meta || (response as any)?.data?.meta || null,
      };
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000, // Keep History/Calls tabs live (matches Recent Activity)
    refetchIntervalInBackground: false, // Only poll when window is focused
    placeholderData: (previousData) => previousData, // Keep previous data while loading
  });

  // Live update: when a call ends anywhere in the app (dialer:call-ended from
  // TwilioDeviceContext), refetch call history immediately AND again 8s later —
  // canceled/no-answer rows are created by Twilio's status webhook a few
  // seconds AFTER the call ends, so the immediate refetch alone would miss them.
  useEffect(() => {
    if (!leadId) return;
    let delayed: ReturnType<typeof setTimeout> | null = null;
    const onCallEnded = () => {
      refetchCallHistory();
      if (delayed) clearTimeout(delayed);
      delayed = setTimeout(() => { refetchCallHistory(); }, 8000);
    };
    window.addEventListener('dialer:call-ended', onCallEnded);
    return () => {
      window.removeEventListener('dialer:call-ended', onCallEnded);
      if (delayed) clearTimeout(delayed);
    };
  }, [leadId, refetchCallHistory]);

  // Fetch email history data using the useEmails hook with pagination
  const { data: emailHistoryData, isLoading: emailHistoryLoading } = useEmails(
    { leadId: leadId || undefined, page: emailPage, limit: emailPageSize },
    { enabled: !!leadId }
  );

  // Extract emails from response
  const emails: EmailHistory[] = useMemo(() => {
    if (!emailHistoryData?.items || !Array.isArray(emailHistoryData.items)) {
      return [];
    }
    return emailHistoryData.items as EmailHistory[];
  }, [emailHistoryData]);

  // Check if OAuth is configured for emails — default false until data loads
  const isOAuthConfigured = useMemo(() => {
    if (!emailHistoryData) return false;
    return emailHistoryData?.meta?.oauthConfigured !== false;
  }, [emailHistoryData]);

  // Trigger email sync when lead details page is opened (on-demand sync)
  // This runs once per lead to sync emails from OAuth provider (Gmail/Microsoft)
  useEffect(() => {
    if (leadId && isOAuthConfigured && !hasTriggeredEmailSync && !syncLeadEmailsMutation.isPending) {
      setHasTriggeredEmailSync(true);
      // Trigger sync in background - don't block UI or show errors
      syncLeadEmailsMutation.mutate(leadId);
    }
  }, [leadId, isOAuthConfigured, hasTriggeredEmailSync, syncLeadEmailsMutation]);

  // Reset sync state when leadId changes (navigating to different lead)
  useEffect(() => {
    setHasTriggeredEmailSync(false);
  }, [leadId]);

  // Manual refresh handler for email sync
  const handleRefreshEmails = async () => {
    if (leadId && !syncLeadEmailsMutation.isPending) {
      await syncLeadEmailsMutation.mutateAsync(leadId);
    }
  };

  const handleCallClick = (call: any) => {
    // Open call details in a new tab or modal
    if (call.id) {
      // Navigate to call details page or open modal
      window.open(`/communication?callId=${call.id}`, '_blank');
    }
  };

  const handlePlayRecording = (url: string) => {
    if (url) {
      // Open recording in a new tab for playback
      window.open(url, '_blank');
    } else {
    }
  };

  const handleEmailClick = (email: EmailHistory) => {
    // Open email details in modal
    setSelectedEmail(email);
    setIsEmailDetailOpen(true);
  };

  return (
    <>
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="inline-flex w-full">
        <TabsTrigger value="details" className="flex-1 min-w-0 px-2">
          <User className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">Details</span>
        </TabsTrigger>
        <TabsTrigger value="notes" className="flex-1 min-w-0 px-2">
          <Notebook className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">Notes</span>
        </TabsTrigger>
        <TabsTrigger value="history" className="flex-1 min-w-0 px-2">
          <History className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">History</span>
        </TabsTrigger>
        <TabsTrigger value="quotes" className="flex-1 min-w-0 px-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
          <InsuraBotIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">Quotes</span>
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex-1 min-w-0 px-2">
          <FileText className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">Docs</span>
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="details" className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Unified General Information Card - combines General, Family, Income, and Custom Fields */}
            <UnifiedGeneralCard isEditing={isEditing} leadData={leadData} leadId={leadId} onCallNumber={callLead} />

            {/* On smaller screens or when dialer is expanded, show right column content in the left column */}
            {showInLeftColumn && (
              <>
                {!!leadId && (
                  <LeadAppointmentsSummary
                    leadId={leadId}
                    leadState={leadData?.state}
                    isEditing={isEditing}
                    onNavigateToHistory={() => setActiveTab('history')}
                  />
                )}
                {!!leadId && (
                  <LeadPolicyDetails
                    hasPolicies={hasPolicies}
                    policies={policies}
                    policyCount={policyCount}
                    leadId={leadId}
                    leadName={leadData?.name}
                    isPrimaryLead={isPrimaryLead}
                    onStatusChange={onStatusChange}
                  />
                )}
                <LeadActivityTimeline leadId={leadId} />
                <LeadInstantQuotes leadId={leadId} lead={leadData} />
                {leadId && (
                  <LeadHouseholdSection
                    leadId={leadId}
                    leadName={leadData?.name}
                    simplified={true}
                    policies={policies}
                  />
                )}
              </>
            )}

            {/* Beneficiary Section - Only show when lead status is Sold & Approved */}
            {leadData?.leadStat === "Sold & Approved" && (
              <Card className="shadow-sm border-gray-200">
                <CardContent className="p-6">
                  <BeneficiarySection isEditing={isEditing} leadId={leadId} />
                </CardContent>
              </Card>
            )}

            {/* ERS Referral Engine - Only show when lead status is Sold & Approved */}
            {leadData?.leadStat === "Sold & Approved" && (
              <Card className="shadow-sm border-gray-200">
                <CardContent className="p-6">
                  <ErsSection leadId={leadId!} leadData={leadData} policies={policies} />
                </CardContent>
              </Card>
            )}

            {/* Lead Management Section */}
            <Card className="shadow-sm border-gray-200">
              <CardContent className="p-6">
                <MiscellaneousSection isEditing={isEditing} leadData={leadData} leadId={leadId} onStatusChange={onStatusChange} />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - only shows content on large screens when dialer is not expanded */}
          {!showInLeftColumn && (
          <div className="space-y-6">
            {/* Appointment Summary */}
            {!!leadId && (
              <LeadAppointmentsSummary
                leadId={leadId}
                leadState={leadData?.state}
                isEditing={isEditing}
                onNavigateToHistory={() => setActiveTab('history')}
              />
            )}

            {/* Policy Details */}
            {!!leadId && (
              <LeadPolicyDetails
                hasPolicies={hasPolicies}
                policies={policies}
                policyCount={policyCount}
                leadId={leadId}
                leadName={leadData?.name}
                isPrimaryLead={isPrimaryLead}
                onStatusChange={onStatusChange}
              />
            )}

            {/* Activity Timeline - moved above Household Members */}
            <LeadActivityTimeline leadId={leadId} />

            {/* Instant Quotes — auto-built carrier tiles with one-click Apply */}
            <LeadInstantQuotes leadId={leadId} lead={leadData} />

            {/* Household Members Section */}
            {leadId && (
              <LeadHouseholdSection
                leadId={leadId}
                leadName={leadData?.name}
                simplified={true}
                policies={policies}
              />
            )}
          </div>
          )}
        </div>
      </TabsContent>

      <TabsContent value="notes" className="mt-6">
        {leadId && (
          <LeadNotes leadId={leadId} initialNotes={initialNotes} lead={leadData} />
        )}
      </TabsContent>

      <TabsContent value="history" className="mt-6">
        {leadId && (
          <LeadHistory
            leadId={leadId}
            leadName={leadData?.name || `${leadData?.first_name || ''} ${leadData?.last_name || ''}`.trim() || undefined}
            leadPhone={leadData?.phone}
            leadState={leadData?.state}
            calls={callHistoryData?.items || []}
            emails={emails}
            isLoadingCalls={callHistoryLoading}
            isLoadingEmails={emailHistoryLoading}
            oauthConfigured={isOAuthConfigured}
            onCallClick={handleCallClick}
            onCallBack={onCallBack}
            onPlayRecording={handlePlayRecording}
            onRefreshCalls={async () => { await refetchCallHistory(); }}
            onEmailClick={handleEmailClick}
            onRefreshEmails={handleRefreshEmails}
            isRefreshingEmails={syncLeadEmailsMutation.isPending}
            callPaginationMeta={callHistoryData?.meta}
            onCallPageChange={setCallPage}
            onCallPageSizeChange={setCallPageSize}
            emailPaginationMeta={emailHistoryData?.meta}
            onEmailPageChange={setEmailPage}
            onEmailPageSizeChange={setEmailPageSize}
            onSmsPageChange={setSmsPage}
            onSmsPageSizeChange={setSmsPageSize}
            onOpenSmsWidget={onOpenSmsWidget}
          />
        )}
      </TabsContent>

      <TabsContent value="documents" className="mt-6">
        {leadId && (
          <LeadDocuments
            leadId={leadId}
            allowMultiple={true}
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
          />
        )}
      </TabsContent>
    </Tabs>

    {/* InsuraBot for Quoting - Opens when Quotes tab is clicked. Enrollment
        context (Techf agencies) renders the pre-filled quote-form card; other
        agencies use the Compulife underwriting/quote flow. */}
    <InsuraBot
      open={isInsuraBotOpen}
      onOpenChange={setIsInsuraBotOpen}
      defaultContext={quoteEnrollEnabled ? "enrollment" : "underwriting"}
      contextMetadata={getLeadContextForInsuraBot()}
    />

    {/* Email Detail Modal - Opens when email is clicked in history */}
    <EmailDetailModal
      open={isEmailDetailOpen}
      onOpenChange={setIsEmailDetailOpen}
      email={selectedEmail}
      leadId={leadId}
    />
    </>
  );
};